import { useEffect, useState } from 'react';

export type Route =
  | { name: 'summary' }
  | { name: 'cabinet' }
  | { name: 'contact' }
  | { name: 'messages' }
  | { name: 'chat' }
  | { name: 'search' }
  | { name: 'payments' }
  | { name: 'appointments' }
  | { name: 'aligners' }
  | { name: 'exercises' }
  | { name: 'photos' }
  | { name: 'signatures' }
  | { name: 'documents' }
  | { name: 'astuces' }
  | { name: 'book' }
  | { name: 'emergency' }
  | { name: 'photo-sessions' };

const routeNames: Route['name'][] = [
  'summary',
  'cabinet',
  'contact',
  'messages',
  'chat',
  'search',
  'payments',
  'appointments',
  'aligners',
  'exercises',
  'photos',
  'signatures',
  'documents',
  'astuces',
  'book',
  'emergency',
  'photo-sessions',
];

export type NavDirection = 'forward' | 'back';

let pendingDirection: NavDirection = 'forward';
let currentPracticeUuid = '';

function parseHash(hash: string): {
  route: Route;
  practice: string;
  searchQuery: string;
  child: string;
} {
  const stripped = hash.replace(/^#\/?/, '');
  const [path, queryStr = ''] = stripped.split('?');
  const params = new URLSearchParams(queryStr);
  const segment = path?.split('/')[0] ?? '';
  const name = routeNames.find((n) => n === segment) ?? 'summary';
  return {
    route: { name },
    practice: params.get('cabinet') ?? '',
    searchQuery: params.get('q') ?? '',
    child: params.get('child') ?? '',
  };
}

/** Lit le `child` actuellement en URL (ou chaîne vide). */
export function getChildFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return parseHash(window.location.hash).child;
}

function buildHash(
  name: Route['name'],
  practice: string,
  extras?: Record<string, string>,
): string {
  const params = new URLSearchParams();
  if (practice) params.set('cabinet', practice);
  if (extras) {
    for (const [k, v] of Object.entries(extras)) {
      if (v) params.set(k, v);
    }
  }
  const qs = params.toString();
  return qs ? `#/${name}?${qs}` : `#/${name}`;
}

export function useRoute(): {
  route: Route;
  direction: NavDirection;
  practiceUuid: string;
  searchQuery: string;
} {
  const initial = parseHash(window.location.hash);
  currentPracticeUuid = initial.practice;
  const [route, setRoute] = useState<Route>(initial.route);
  const [practiceUuid, setPracticeUuid] = useState<string>(initial.practice);
  const [searchQuery, setSearchQuery] = useState<string>(initial.searchQuery);
  const [direction, setDirection] = useState<NavDirection>('forward');

  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = buildHash('summary', currentPracticeUuid);
    }

    const onPopState = () => {
      pendingDirection = 'back';
    };
    const onHashChange = () => {
      const parsed = parseHash(window.location.hash);
      currentPracticeUuid = parsed.practice;
      setDirection(pendingDirection);
      pendingDirection = 'forward';
      setRoute(parsed.route);
      setPracticeUuid(parsed.practice);
      setSearchQuery(parsed.searchQuery);
    };

    window.addEventListener('popstate', onPopState);
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  useEffect(() => {
    if (direction === 'forward') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [route.name, direction]);

  return { route, direction, practiceUuid, searchQuery };
}

export function navigate(
  name: Route['name'],
  extras?: Record<string, string>,
): void {
  pendingDirection = 'forward';
  window.location.hash = buildHash(name, currentPracticeUuid, extras);
}

export function back(): void {
  pendingDirection = 'back';
  window.history.back();
}

/** Met à jour le cabinet courant dans l'URL (sans changer de route). */
export function setPracticeInUrl(uuid: string): void {
  currentPracticeUuid = uuid;
  const parsed = parseHash(window.location.hash);
  // direction "forward" car c'est un choix utilisateur
  pendingDirection = 'forward';
  // remplace l'entrée actuelle sans empiler dans l'historique
  const newHash = buildHash(parsed.route.name, uuid);
  if (window.location.hash !== newHash) {
    history.replaceState(null, '', newHash);
    // forcer le hashchange manuellement vu qu'on a utilisé replaceState
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
}
